"use client";

import React from "react";
import { ExternalLink, FileText, Package } from "lucide-react";
import { formatCurrency } from "@wtc/ui";
import type { Offering } from "@/lib/types/database/offerings";
import { cn } from "@/lib/utils";
import { ClampedText } from "@/components/shared/ClampedText";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";

interface OfferingDetailsTabProps {
  item: Offering;
}

// Thin wrapper over the canonical `@wtc/ui` formatter. The result is rendered in
// JSX, so a non-null string is required — coalesce to "". Preserves the prior
// Intl default of 2 fixed fraction digits for USD.
function formatUsd(value: number): string {
  return formatCurrency(value, { fallback: "", minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "";
}

export function OfferingDetailsTab({ item }: OfferingDetailsTabProps) {
  const staffItems = Array.isArray(item.staff_line_items) ? item.staff_line_items : [];
  const hardCostItems = Array.isArray(item.hard_cost_line_items) ? item.hard_cost_line_items : [];

  return (
    <div className="space-y-6 pr-1">
      <section
        className={cn(
          "rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-b from-white to-slate-50/80 dark:from-gray-900 dark:to-gray-900 p-5 shadow-sm",
          "ring-1 ring-black/[0.03]"
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">
              <Package className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 space-y-2">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{item.title}</h2>
              {item.pricing_display ? (
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">{item.pricing_display}</p>
              ) : null}
              {item.pricing_model ? (
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Model: {item.pricing_model}
                </p>
              ) : null}
              {item.executive_summary ? (
                <ClampedText
                  text={item.executive_summary}
                  lines={3}
                  className="text-sm leading-relaxed text-gray-600 dark:text-gray-400"
                />
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {item.linked_project_slug ? (
              <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                {item.linked_project_slug}
              </span>
            ) : null}
            {item.cta_href && item.cta_text ? (
              <a
                href={item.cta_href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                {item.cta_text}
                <ExternalLink className="h-3.5 w-3.5 opacity-90" aria-hidden />
              </a>
            ) : null}
          </div>
        </div>
      </section>

      {item.image_url ? (
        <section className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
          {/* eslint-disable-next-line @next/next/no-img-element -- public absolute offering image_url */}
          <img
            src={item.image_url}
            alt=""
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </section>
      ) : null}

      {item.description ? (
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />
            <h3 className="text-sm font-semibold">Description</h3>
          </div>
          <ClampedText
            text={item.description}
            lines={4}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
          />
        </section>
      ) : null}

      {item.tags && item.tags.length > 0 ? (
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm lg:col-span-1">
          <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Cost summary</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
              <dt className="text-gray-600 dark:text-gray-400">Total hours</dt>
              <dd className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {Number(item.total_hours || 0).toFixed(1)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
              <dt className="text-gray-600 dark:text-gray-400">Hard costs</dt>
              <dd className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {formatUsd(Number(item.total_hard_costs || 0))}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1">
              <dt className="text-base font-semibold text-gray-900 dark:text-gray-100">Total cost</dt>
              <dd className="text-base font-bold tabular-nums text-blue-900 dark:text-blue-200">
                {formatUsd(Number(item.total_cost || 0))}
              </dd>
            </div>
          </dl>
          {item.base_price != null && Number(item.base_price) > 0 ? (
            <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:text-gray-400">
              Base price (list):{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">{formatUsd(Number(item.base_price))}</span>
            </p>
          ) : null}
        </section>

        <div className="space-y-6 lg:col-span-2">
          {staffItems.length > 0 ? (
            <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
              <CollapsibleSection title="Staff line items" count={staffItems.length} defaultCollapsed className="border-t-0 pt-0">
              <ul className="divide-y divide-gray-100">
                {staffItems.map(
                  (li: { role?: string; hours?: number; rate?: number }, i: number) => (
                    <li
                      key={i}
                      className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm first:pt-0 last:pb-0"
                    >
                      <span className="text-gray-800 dark:text-gray-200">
                        <span className="font-medium">{li.role || "Role"}</span>
                        <span className="text-gray-500 dark:text-gray-400"> · {Number(li.hours || 0).toFixed(1)} hrs</span>
                      </span>
                      {li.rate != null ? (
                        <span className="tabular-nums text-gray-600 dark:text-gray-400">{formatUsd(Number(li.rate))}/hr</span>
                      ) : null}
                    </li>
                  )
                )}
              </ul>
              </CollapsibleSection>
            </section>
          ) : null}

          {hardCostItems.length > 0 ? (
            <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
              <CollapsibleSection title="Hard costs" count={hardCostItems.length} defaultCollapsed className="border-t-0 pt-0">
              <ul className="divide-y divide-gray-100">
                {hardCostItems.map((li: { description?: string; amount?: number }, i: number) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-4 py-3 text-sm first:pt-0 last:pb-0"
                  >
                    <span className="text-gray-800 dark:text-gray-200">{li.description || "Item"}</span>
                    <span className="shrink-0 font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                      {formatUsd(Number(li.amount || 0))}
                    </span>
                  </li>
                ))}
              </ul>
              </CollapsibleSection>
            </section>
          ) : null}

          {item.one_pager_pdf_url ? (
            <section className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900 p-5">
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">One-pager</h3>
              <a
                href={item.one_pager_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 hover:underline"
              >
                Open PDF
                <ExternalLink className="h-4 w-4" aria-hidden />
              </a>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
